/**
 * Observability Service
 * Integrates Datadog RUM, Dynatrace OneAgent, and Splunk RUM
 * for APM-level monitoring, log aggregation, infrastructure metrics, and tracing.
 */

const DATADOG_SITE = import.meta.env?.VITE_DATADOG_SITE || 'datadoghq.com';
const DATADOG_APP_ID = import.meta.env?.VITE_DATADOG_APPLICATION_ID || '';
const DATADOG_CLIENT_TOKEN = import.meta.env?.VITE_DATADOG_CLIENT_TOKEN || '';

const DYNATRACE_ENV_ID = import.meta.env?.VITE_DYNATRACE_ENV_ID || '';
const DYNATRACE_APP_ID = import.meta.env?.VITE_DYNATRACE_APP_ID || '';

const SPLUNK_REALM = import.meta.env?.VITE_SPLUNK_REALM || 'us0';
const SPLUNK_RUM_TOKEN = import.meta.env?.VITE_SPLUNK_RUM_ACCESS_TOKEN || '';

const APP_NAME = 'ora-fusion-ai';
const APP_VERSION = import.meta.env?.VITE_APP_VERSION || '1.0.0';
const APP_ENV = import.meta.env?.MODE || 'production';

// ─── Initialization State ────────────────────────────────────────────────────
const state = {
  datadog: { initialized: false, enabled: !!DATADOG_APP_ID && !!DATADOG_CLIENT_TOKEN },
  dynatrace: { initialized: false, enabled: !!DYNATRACE_ENV_ID && !!DYNATRACE_APP_ID },
  splunk: { initialized: false, enabled: !!SPLUNK_RUM_TOKEN },
};

// ─── Script Loader Helper ─────────────────────────────────────────────────────
function loadScript(src, onLoad, onError) {
  if (document.querySelector(`script[src="${src}"]`)) {
    onLoad && onLoad();
    return;
  }
  const script = document.createElement('script');
  script.src = src;
  script.crossOrigin = 'anonymous';
  script.async = true;
  script.onload = onLoad || null;
  script.onerror = onError || null;
  document.head?.appendChild(script);
}

// ─── Datadog RUM ──────────────────────────────────────────────────────────────
function initDatadog() {
  if (!state?.datadog?.enabled || state?.datadog?.initialized) return;

  const site = DATADOG_SITE?.replace(/^https?:\/\//, '');
  const siteKey = site?.includes('us3') ? 'us3' : site?.includes('us5') ? 'us5' : site?.includes('eu') ? 'eu1' : 'us1';
  const cdnUrl = `https://www.datadoghq-browser-agent.com/${siteKey}/v6/datadog-rum.js`;

  loadScript(cdnUrl, () => {
    try {
      if (window.DD_RUM) {
        window.DD_RUM?.init({
          applicationId: DATADOG_APP_ID,
          clientToken: DATADOG_CLIENT_TOKEN,
          site: DATADOG_SITE,
          service: APP_NAME,
          env: APP_ENV,
          version: APP_VERSION,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 0,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: 'mask-user-input',
        });
        state.datadog.initialized = true;
        console.info('[Observability] Datadog RUM initialized');
      }
    } catch (err) {
      console.warn('[Observability] Datadog RUM init error:', err?.message);
    }
  }, () => {
    console.warn('[Observability] Datadog RUM script failed to load');
  });
}

// ─── Dynatrace OneAgent ───────────────────────────────────────────────────────
function initDynatrace() {
  if (!state?.dynatrace?.enabled || state?.dynatrace?.initialized) return;

  const scriptUrl = `https://${DYNATRACE_ENV_ID}.live.dynatrace.com/api/v2/rum/oneAgentJavaScriptTag/${DYNATRACE_APP_ID}`;

  loadScript(scriptUrl, () => {
    state.dynatrace.initialized = true;
    console.info('[Observability] Dynatrace OneAgent initialized');
  }, () => {
    console.warn('[Observability] Dynatrace OneAgent script failed to load');
  });
}

// ─── Splunk RUM ───────────────────────────────────────────────────────────────
function initSplunk() {
  if (!state?.splunk?.enabled || state?.splunk?.initialized) return;

  const cdnUrl = `https://cdn.observability.splunkcloud.com/o11y-gdi-rum/v1/splunk-otel-web.js`;

  loadScript(cdnUrl, () => {
    try {
      if (window.SplunkRum) {
        window.SplunkRum?.init({
          realm: SPLUNK_REALM,
          rumAccessToken: SPLUNK_RUM_TOKEN,
          applicationName: APP_NAME,
          version: APP_VERSION,
          deploymentEnvironment: APP_ENV,
        });
        state.splunk.initialized = true;
        console.info('[Observability] Splunk RUM initialized');
      }
    } catch (err) {
      console.warn('[Observability] Splunk RUM init error:', err?.message);
    }
  }, () => {
    console.warn('[Observability] Splunk RUM script failed to load');
  });
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Initialize all configured observability platforms.
 * Call once at app startup (e.g., in index.jsx or App.jsx).
 */
export function initObservability() {
  initDatadog();
  initDynatrace();
  initSplunk();
}

/**
 * Set the authenticated user context on all active platforms.
 * @param {{ id: string, email?: string, name?: string, role?: string }} user
 */
export function setObservabilityUser(user) {
  if (!user?.id) return;

  // Datadog — send only id + role (no PII/PHI)
  if (state?.datadog?.initialized && window.DD_RUM) {
    try {
      window.DD_RUM?.setUser({ id: user?.id, role: user?.role });
    } catch (_) {}
  }

  // Splunk — send only id + role (no PII/PHI)
  if (state?.splunk?.initialized && window.SplunkRum) {
    try {
      window.SplunkRum?.setGlobalAttributes({ 'user.id': user?.id, 'user.role': user?.role || '' });
    } catch (_) {}
  }
}

/**
 * Track a custom action / event across all platforms.
 * @param {string} name  Event name
 * @param {object} [context]  Additional key-value context
 */
export function trackObservabilityEvent(name, context = {}) {
  // Datadog
  if (state?.datadog?.initialized && window.DD_RUM) {
    try {
      window.DD_RUM?.addAction(name, context);
    } catch (_) {}
  }

  // Splunk — custom span via OpenTelemetry API exposed by SplunkRum
  if (state?.splunk?.initialized && window.SplunkRum) {
    try {
      const tracer = window.SplunkRum?.provider?.getTracer?.('ora-fusion-ai');
      if (tracer) {
        const span = tracer?.startSpan(name);
        Object.entries(context)?.forEach(([k, v]) => span?.setAttribute(k, String(v)));
        span?.end();
      }
    } catch (_) {}
  }
}

/**
 * Report an error to all platforms.
 * @param {Error|string} error
 * @param {object} [context]
 */
export function reportObservabilityError(error, context = {}) {
  // Datadog
  if (state?.datadog?.initialized && window.DD_RUM) {
    try {
      window.DD_RUM?.addError(error, context);
    } catch (_) {}
  }

  // Splunk — errors are auto-captured; add global attribute for context
  if (state?.splunk?.initialized && window.SplunkRum) {
    try {
      window.SplunkRum?.setGlobalAttributes({ 'error.context': JSON.stringify(context) });
    } catch (_) {}
  }
}

/**
 * Send load test telemetry to all platforms.
 * @param {{ status: string, users: number, rps: number, p99: number, errorRate: number }} metrics
 */
export function sendLoadTestTelemetry(metrics) {
  const context = {
    'load_test.status': metrics?.status || 'unknown',
    'load_test.concurrent_users': metrics?.users || 0,
    'load_test.rps': metrics?.rps || 0,
    'load_test.p99_ms': metrics?.p99 || 0,
    'load_test.error_rate_pct': metrics?.errorRate || 0,
    'load_test.timestamp': new Date()?.toISOString(),
  };

  trackObservabilityEvent('load_test.metrics', context);
}

/**
 * Send AI agent activity telemetry to all platforms.
 * @param {{ agent: string, action: string, durationMs: number, success: boolean }} activity
 */
export function sendAIAgentTelemetry(activity) {
  const context = {
    'ai.agent': activity?.agent || 'unknown',
    'ai.action': activity?.action || 'unknown',
    'ai.duration_ms': activity?.durationMs || 0,
    'ai.success': activity?.success ? 'true' : 'false',
    'ai.timestamp': new Date()?.toISOString(),
  };

  trackObservabilityEvent('ai_agent.activity', context);
}

/**
 * Send audit log event telemetry to all platforms.
 * @param {{ eventType: string, userId: string, resource: string, outcome: string }} auditEvent
 */
export function sendAuditLogTelemetry(auditEvent) {
  const context = {
    'audit.event_type': auditEvent?.eventType || 'unknown',
    'audit.user_id': auditEvent?.userId || 'anonymous',
    'audit.resource': auditEvent?.resource || 'unknown',
    'audit.outcome': auditEvent?.outcome || 'unknown',
    'audit.timestamp': new Date()?.toISOString(),
  };

  trackObservabilityEvent('audit.log_event', context);
}

/**
 * Returns the current initialization status of all platforms.
 */
export function getObservabilityStatus() {
  return {
    datadog: {
      enabled: state?.datadog?.enabled,
      initialized: state?.datadog?.initialized,
      configured: !!DATADOG_APP_ID && !!DATADOG_CLIENT_TOKEN,
    },
    dynatrace: {
      enabled: state?.dynatrace?.enabled,
      initialized: state?.dynatrace?.initialized,
      configured: !!DYNATRACE_ENV_ID && !!DYNATRACE_APP_ID,
    },
    splunk: {
      enabled: state?.splunk?.enabled,
      initialized: state?.splunk?.initialized,
      configured: !!SPLUNK_RUM_TOKEN,
    },
  };
}
